import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import Link from "@khanacademy/wonder-blocks-link";
import {CircularSpinner} from "@khanacademy/wonder-blocks-progress-spinner";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {
    HeadingLarge,
    LabelMedium,
    LabelSmall,
} from "@khanacademy/wonder-blocks-typography";
import {
    withMarkConversion,
    type WithMarkConversionProps,
    type WithoutMarkConversion,
} from "@khan/analytics-learning-time";
import {getSelectedDateRange} from "@khan/component-date-range-dropdown";
import {EmptyState} from "@khan/component-simple-table";

import ActivitySessionsReport, {
    DEFAULT_LEARNER_DATE_RANGE_VALUE,
    LIHP_INDV_STUDENT_REPORT,
} from "#districts-classroom-package/activity-sessions-report/activity-sessions-report.tsx";

type Props = {
    kaid: string | null | undefined;
    isKmapSynced: boolean;
    isMyPage: boolean;
} & WithMarkConversionProps;

class Progress extends React.Component<Props> {
    componentDidMount() {
        this.props.markConversion({id: "logged_in_homepage_visit"});
    }

    render(): React.ReactNode {
        const {kaid, isKmapSynced, isMyPage, markConversion} = this.props;
        const filters = {
            dateRangeSelection: {
                ...getSelectedDateRange(null, DEFAULT_LEARNER_DATE_RANGE_VALUE),
            },
            selectedCourseFilter: null,
        } as const;

        const href = "https://khanacademy.zendesk.com/hc/articles/360032987591";

        return (
            <View testId="progress">
                <HeadingLarge>
                    {isMyPage ? t`My progress` : t`Progress`}{" "}
                    <Link
                        href={href}
                        onClick={() => {
                            markConversion({
                                id: "user_clicked_on_learn_more_link",
                            });
                        }}
                    >
                        <LabelSmall style={styles.displayInline}>
                            {t`Learn more.`}
                        </LabelSmall>
                    </Link>
                </HeadingLarge>
                <LabelMedium style={styles.delayText}>
                    {t`Latest activity may take 10 mins to show below.`}
                </LabelMedium>
                <Strut size={spacing.large_24} />
                {kaid ? (
                    <ActivitySessionsReport
                        studentKaid={kaid}
                        isNweaMapSynced={isKmapSynced}
                        emptyState={
                            <EmptyState
                                title={t`No progress yet!`}
                                message={t`When we see some activity, progress will show up here.`}
                            />
                        }
                        pageSource={LIHP_INDV_STUDENT_REPORT}
                        {...filters}
                    />
                ) : (
                    <View style={styles.spinnerContainer}>
                        <CircularSpinner />
                    </View>
                )}
                <Strut size={spacing.large_24} />
            </View>
        );
    }
}

const styles = StyleSheet.create({
    displayInline: {
        display: "inline",
    },
    spinnerContainer: {
        flex: 1,
        flexDirection: "row",
        justifyContent: "center",
    },
    delayText: {
        marginTop: spacing.small_12,
        color: color.offBlack64,
    },
});

type ExportProps = WithoutMarkConversion<
    JSX.LibraryManagedAttributes<
        typeof Progress,
        React.ComponentProps<typeof Progress>
    >
>;

const ProgressWithLearningTime: React.ComponentType<ExportProps> =
    withMarkConversion(Progress);
export default ProgressWithLearningTime;
